import { useEffect, useRef, useState } from "react";
import { colors } from '../../../theme/colors';
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  useValidateInterviews,
  useUploadResponseVideo,
} from "../../../store/query/scheduleInterviewQuery";
import {
  LeftSection,
  RightSection,
  Title,
  IntroText,
  LiveVideo,
  RecordedVideoWrapper,
  WelcomeWrapper,
  CountdownWrapper,
  InterviewWelcomeButton,
  VideoContainer,
  WelcomeInterview,
  WelcomeViewer,
  RecIndicator,
  MicIconBadge,
  RecIconBadge,
} from "./style";
import MicIcon from "@mui/icons-material/Mic";
import { CheckCircle, RadioButtonUnchecked } from "@mui/icons-material";
export default function InterviewWelcome({
  interviewDetail: propInterviewDetail,
  onStart,
  startRecording = false,
  startInterview = () => {},
  showSnackbar,
  enterFullScreen,
}) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const validateInterview = useValidateInterviews();
  const uploadReferenceVideoMutation = useUploadResponseVideo();
  const [candidateDetail, setCandidateDetail] = useState(null);
  const [stream, setStream] = useState(null);
  const [recordedURL, setRecordedURL] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isRecorded, setIsRecorded] = useState(false);
  const [isInterviewBlocked, setIsInterviewBlocked] = useState(false);
  const [countdown, setCountdown] = useState(12);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const videoRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const countdownInterval = useRef(null);

  useEffect(() => {
    if (!token) return;
    validateInterview.mutate(
      { token },
      {
        onSuccess: (data) => {
          setCandidateDetail(data.interviewDetail);
        },
      }
    );
  }, [token]);

  useEffect(() => {
    let localStream;
    let cancelled = false;
    const enableCamera = async () => {
      try {
        const acquiredStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        if (cancelled) {
          acquiredStream.getTracks().forEach((track) => track.stop());
          return;
        }
        localStream = acquiredStream;
        setStream(acquiredStream);
        if (videoRef.current) {
          videoRef.current.srcObject = acquiredStream;
          videoRef.current.play().catch(() => {});
        }
      } catch (err) {
        showSnackbar("Camera or microphone permission denied", "error");
      }
    };

    enableCamera();
    return () => {
      cancelled = true;
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current
        .play()
        .catch((err) => console.error("Auto-play error:", err));
    }
  }, [stream]);

  useEffect(() => {
    if (
      !startRecording ||
      !stream ||
      isRecording ||
      isRecorded ||
      isInterviewBlocked
    )
      return;

    chunksRef.current = [];
    setIsRecording(true);
    setCountdown(18);

    const recorder = new MediaRecorder(stream);
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onerror = (event) => {
      console.error("Reference video recorder error:", event.error);
      clearInterval(countdownInterval.current);
      setIsRecording(false);
      showSnackbar(
        "Recording failed. Please refresh the page and try again.",
        "error",
      );
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      const videoURL = URL.createObjectURL(blob);
      setRecordedURL(videoURL);
      setIsRecording(false);
      setIsRecorded(true);
      setCountdown(0);
      clearInterval(countdownInterval.current);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
    };

    recorder.start();

    // Countdown timer
    // countdownInterval.current = setInterval(() => {
    //   setCountdown((prev) => {
    //     if (prev <= 1) {
    //       recorder.stop();
    //       return 0;
    //     }
    //     return prev - 1;
    //   });
    // }, 1000);

    // setTimeout(() => {
    //   if (recorder.state === "recording") recorder.stop();
    // }, 12000);

    countdownInterval.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval.current);
          if (recorder.state === "recording") recorder.stop();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [startRecording, stream, isRecording, isRecorded]);

  const handleProceed = () => {
    if (isInterviewBlocked) {
      showSnackbar("Your interview has been blocked.", "error");
      return;
    }
    if (chunksRef.current.length === 0) {
      showSnackbar("Recording not completed yet. Please wait...", "warning");
      return;
    }

    const candidateId = propInterviewDetail?._id || candidateDetail?._id;
    if (!candidateId) {
      showSnackbar("Candidate ID not found. Please refresh the page.", "error");
      return;
    }

    if (enterFullScreen) {
      enterFullScreen().catch((err) =>
        console.log("Fullscreen failed to trigger:", err),
      );
    }

    const blob = new Blob(chunksRef.current, { type: "video/webm" });
    const formData = new FormData();
    formData.append("reference_video", blob, "reference_video.webm");
    formData.append("candidate_instance_id", candidateId);

    uploadReferenceVideoMutation.mutate(formData, {
      onSuccess: () => {
        setIsSubmitted(true);
        onStart();
        startInterview();
      },
      onError: (err) => {
        const backendMessage =
          err?.response?.data?.detail || err?.response?.data?.message;
        showSnackbar(
          backendMessage
            ? `Failed to upload video: ${backendMessage}`
            : "Failed to upload video. Please check your connection and try again.",
          "error",
        );
      },
    });
  };

  const isLoading = uploadReferenceVideoMutation.isPending;
  return (
    <WelcomeWrapper>
      <Box className="welcome_wrapper">
        <WelcomeViewer>
          {["Welcome", "Camera", "Audio", "Interview", "Start"].map(
            (step, index) => (
              <Box
                key={step}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                {(() => {
                  if (index <= 2) {
                    return <CheckCircle color="success" />;
                  }

                  if (index === 3) {
                    return isRecorded ? (
                      <CheckCircle color="success" />
                    ) : (
                      <RadioButtonUnchecked sx={{ color: "#aaa" }} />
                    );
                  }
                  if (index === 4) {
                    return isSubmitted ? (
                      <CheckCircle color="success" />
                    ) : (
                      <RadioButtonUnchecked sx={{ color: "#aaa" }} />
                    );
                  }
                })()}

                <Typography variant="body2">{step}</Typography>

                {index < 4 && (
                  <ArrowRightAltIcon fontSize="small" sx={{ color: "#aaa" }} />
                )}
              </Box>
            )
          )}

          <Box sx={{ ml: "auto", fontWeight: 700, color: colors.primary }}>
            Step {isSubmitted ? 5 : isRecorded ? 4 : 3} of 5
          </Box>
        </WelcomeViewer>

        <WelcomeInterview>
          <LeftSection>
            <VideoContainer>
              <LiveVideo ref={videoRef} autoPlay muted playsInline />
              <RecIconBadge isrecording={isRecording}>
                {(isRecording || isRecorded) && (
                  <RecIndicator>
                    <FiberManualRecordIcon fontSize="small" /> Rec
                  </RecIndicator>
                )}
              </RecIconBadge>

              <MicIconBadge recording={isRecording}>
                <MicIcon
                  sx={{ color: "#fff", width: "36px", height: "36px" }}
                />
              </MicIconBadge>
            </VideoContainer>
            {/* {recordedURL && (
              <RecordedVideoWrapper>
                <Typography variant="subtitle1" gutterBottom>
                  Verification Recording (Preview)
                </Typography>
                <video
                  src={recordedURL}
                  controls
                  style={{
                    width: "100%",
                    maxHeight: "50vh",
                    borderRadius: 12,
                    border: "1px solid #ddd",
                  }}
                />
              </RecordedVideoWrapper>
            )} */}
          </LeftSection>

          <RightSection>
            <Title variant="h4">Audio & Identity Verification</Title>
            <IntroText>
              Please read the script aloud for{" "}
              <strong>at least 18 seconds</strong> to verify your audio and
              identity.
              <br />
              <strong>Note:</strong> Say your actual phone number instead of
              XXX-XXX-XXXX.
            </IntroText>
            {isRecording && (
              <CountdownWrapper>
                <Box className="countdown_content">
                  <CircularProgress
                    variant="determinate"
                    value={(countdown / 18) * 100}
                    size={40}
                    thickness={5}
                    sx={{
                      color: colors.primary,
                    }}
                  />
                  <Typography variant="h6" fontWeight={700}>
                    {countdown}s
                  </Typography>
                </Box>
                <Box className="countdown_content">
                  <Box
                    className="rightcount"
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <Typography variant="h6">
                      Recording in progress...
                    </Typography>
                    <Typography variant="body2">
                      Keep speaking until the timer ends
                    </Typography>
                  </Box>
                </Box>
              </CountdownWrapper>
            )}

            <Box
              sx={{
                background: colors.primaryLight,
                p: 3,
                borderRadius: 3,
                border: `1px solid ${colors.primaryLightBorder}`,
                mt: 1.9,
              }}
            >
              <Typography variant="h6" className="welcome_title" gutterBottom>
                Welcome {candidateDetail?.candidateName || "Candidate"}
              </Typography>
              <Typography lineHeight={1.8}>
                Hi, my name is{" "}
                <strong>{candidateDetail?.candidateName || ""}</strong>. I’m
                here for the <strong>{candidateDetail?.applyFor || ""}</strong>{" "}
                position. Thank you for this opportunity; I’m looking forward to
                the interview. I confirm that I’m ready to proceed and
                understand the interview guidelines. My phone number for
                validation is <strong>XXX-XXX-XXXX</strong>.
              </Typography>
            </Box>
            <InterviewWelcomeButton
              variant="contained"
              size="large"
              fullWidth={window.innerWidth < 900}
              disabled={!isRecorded || isInterviewBlocked || isLoading}
              onClick={() => {
                handleProceed();
              }}
              sx={{ mt: 1.9, py: 1.5, fontSize: "1.1rem" }}
            >
              {isInterviewBlocked ? (
                "Interview Blocked"
              ) : isLoading ? (
                <>
                  Uploading...{" "}
                  <CircularProgress
                    size={24}
                    sx={{
                      ml: 1,
                      textTransform: "capitalize",
                      color: "inherit",
                    }}
                  />
                </>
              ) : (
                "Proceed to Interview"
              )}
            </InterviewWelcomeButton>
          </RightSection>
        </WelcomeInterview>
      </Box>
    </WelcomeWrapper>
  );
}
